/**
 * MessageBubble Component
 * Singola bolla messaggio con media, reply, status e context menu
 */

import { memo, useCallback, useState, useMemo, ReactNode, useRef } from 'react';
import { Ban, Lock, RefreshCw, Check, FileText, ChevronDown, ChevronUp, ExternalLink, Clock, AlertCircle, RotateCcw, Star, Smile, Reply } from 'lucide-react';
import toast from 'react-hot-toast';
import { MessageStatus } from './MessageStatus';
import { MessageTime } from './MessageTime';
import { MessageMedia } from './MessageMedia';
import { MessageReply } from './MessageReply';
import { MessageReactions } from './MessageReactions';
import { MessageContextMenu } from './MessageContextMenu';
import { ReactionPicker } from './ReactionPicker';
import { isMyReaction, nextReactionEmoji, reactionSenderName } from './reactionsView';
import { getSenderColor, formatSenderName } from '@/lib/utils/whatsappHelpers';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { WHATSAPP_AUDIO_TRANSCRIPTION_ENABLED } from '@/lib/featureFlags';
import { getChatCapabilities, type ChatCapabilities } from '@/lib/services/whatsapp/chatCapabilities';
import { useMessageMarksStore } from './messageMarksStore';
import { CallLogBubble } from './CallLogBubble';
import { LocationContent, ContactContent, PollContent } from './SpecialMessageContent';
import type { Message, PendingMessage } from '@/lib/types/whatsappChat';
import { debugLog } from '../chat/ChatDebugPanel';
import { tip } from '@/components/messages/ChatTooltip';
import { useApp } from '@/components/AppProvider';

interface MessageBubbleProps {
  message: Message | PendingMessage;
  isGroup?: boolean;
  onReply?: (message: Message) => void;
  onMediaClick?: (mediaUrl: string) => void;
  onRefreshMessage?: (messageId: string) => void;
  onForward?: (messageId: string) => void;
  onForwardToTeamChat?: (message: Message) => void;
  onRetry?: (tempId: string) => void;
  chatProfilePicUrl?: string;
  chatName?: string;
  // Mappa nomi risolti (numero → nome) per sender names e menzioni LID in gruppi
  resolvedMentionNames?: Map<string, string>;
  // Selection mode props
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelectionToggle?: (messageId: string, shiftKey: boolean) => void;
  /** Voce "Seleziona" del menu: entra nella selezione multipla da questo messaggio. */
  onStartSelection?: (messageId: string) => void;
  /** Azioni disponibili per il numero attivo (calcolate una volta in MessageList). */
  capabilities?: ChatCapabilities;
}

// Senza capacita' passate: numero Standard con motore ignoto (si prova, 501 spegne).
const DEFAULT_CAPABILITIES = getChatCapabilities({ serviceName: 'waha' });
const SPECIAL_TYPES = new Set(['location', 'contact', 'poll']);

function isPending(msg: Message | PendingMessage): msg is PendingMessage {
  return 'tempId' in msg;
}

/**
 * Costruisce mappa numero → nome da JID names map.
 * Cerca con tutti i suffissi possibili (@c.us, @lid, @s.whatsapp.net).
 */
function buildNumberToNameMap(jidNamesMap: Map<string, string>): Map<string, string> {
  const map = new Map<string, string>();
  if (!jidNamesMap || typeof jidNamesMap[Symbol.iterator] !== 'function') return map;
  for (const [jid, name] of jidNamesMap) {
    // Supporta sia JID (numero@c.us) sia numeri puri (da resolvedMentionNames)
    const number = jid.includes('@') ? jid.split('@')[0] : jid;
    if (!map.has(number)) {
      map.set(number, name);
    }
  }
  return map;
}

// ============ LINK RENDERING ============

// Regex per URL - cattura http(s), www, e domini nudi con path
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"')\]]+/gi;

/**
 * Accorcia un URL per la visualizzazione.
 * Mostra dominio + inizio path, max ~40 chars.
 */
function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url.startsWith('www.') ? `https://${url}` : url);
    const domain = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    const display = domain + path;
    if (display.length > 45) {
      return display.slice(0, 42) + '...';
    }
    return display;
  } catch {
    if (url.length > 45) return url.slice(0, 42) + '...';
    return url;
  }
}

/**
 * Componente link inline: mostra URL accorciato con icona expand on hover.
 */
function InlineLink({ url, isOutbound }: { url: string; isOutbound: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const href = url.startsWith('www.') ? `https://${url}` : url;
  const short = shortenUrl(url);
  const isLong = short !== url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

  return (
    <span className="inline-flex items-center gap-0 group/link">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline underline-offset-2 decoration-1 break-all ${
          isOutbound
            ? 'text-[#027EB5] dark:text-[#53BDEB] decoration-[#027EB5]/40 dark:decoration-[#53BDEB]/40 hover:decoration-[#027EB5] dark:hover:decoration-[#53BDEB]'
            : 'text-[#2383E2] decoration-[#2383E2]/40 hover:decoration-[#2383E2]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {expanded ? url : short}
      </a>
      {isLong && !expanded && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          className={`ml-0.5 opacity-0 group-hover/link:opacity-100 transition-opacity p-0 inline-flex items-center ${
            isOutbound ? 'text-[#027EB5]/60 dark:text-[#53BDEB]/60 hover:text-[#027EB5] dark:hover:text-[#53BDEB]' : 'text-[#2383E2]/50 hover:text-[#2383E2]'
          }`}
          {...tip('Mostra URL completo')}
        >
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
      {expanded && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
          className={`ml-0.5 inline-flex items-center ${
            isOutbound ? 'text-[#027EB5]/60 dark:text-[#53BDEB]/60 hover:text-[#027EB5] dark:hover:text-[#53BDEB]' : 'text-[#2383E2]/50 hover:text-[#2383E2]'
          }`}
          {...tip('Accorcia URL')}
        >
          <ChevronUp className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

/**
 * Renderizza il contenuto testuale con link cliccabili + menzioni.
 * Unifica la logica di link e menzioni in un unico passaggio.
 */
function renderContentWithLinks(
  content: string,
  isOutbound: boolean,
  jidNamesMap?: Map<string, string>,
  isGroup?: boolean
): ReactNode {
  if (!content) return content;

  // Costruisci mappa menzioni se serve
  const numberToName = isGroup && jidNamesMap ? buildNumberToNameMap(jidNamesMap) : null;

  // Pattern combinato: URL o @NUMERO
  const mentionPart = '(@\\d{5,})';
  const urlPart = '(?:https?:\\/\\/|www\\.)[^\\s<>"\'\\)\\]]+';
  const combinedRegex = new RegExp(`(${urlPart})|${mentionPart}`, 'gi');

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Testo prima del match
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // URL match
      parts.push(<InlineLink key={key++} url={match[1]} isOutbound={isOutbound} />);
    } else if (match[2] && numberToName) {
      // Mention match
      const number = match[2].slice(1); // rimuovi @
      const displayName = numberToName.get(number);
      parts.push(
        <span
          key={key++}
          className={`font-semibold ${isOutbound ? 'text-[#027EB5] dark:text-[#53BDEB]' : 'text-[#2383E2]'}`}
        >
          @{displayName || number}
        </span>
      );
    } else {
      // Mention match ma no mappa → mantieni testo originale
      parts.push(match[0]);
    }

    lastIndex = match.index + match[0].length;
  }

  // Se nessun match, ritorna il contenuto originale
  if (parts.length === 0) return content;

  // Testo rimanente
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return <>{parts}</>;
}

/**
 * Parsa il contenuto del messaggio e sostituisce @NUMERO con il nome della persona.
 * Funziona sia con mentionedJidList (da WAHA) sia senza (fallback su nomi noti).
 */
function renderMentionContent(
  content: string,
  jidNamesMap: Map<string, string>,
  isOutbound: boolean
): ReactNode {
  // Cerca pattern @NUMERO (almeno 5 cifre per evitare falsi positivi)
  if (!/@\d{5,}/.test(content)) return content;

  const numberToName = buildNumberToNameMap(jidNamesMap);

  const mentionPattern = /@(\d{5,})/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionPattern.exec(content)) !== null) {
    const number = match[1];
    const displayName = numberToName.get(number);

    // Aggiungi testo prima della menzione
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    parts.push(
      <span
        key={match.index}
        className={`font-semibold ${isOutbound ? 'text-[#027EB5] dark:text-[#53BDEB]' : 'text-[#2383E2]'}`}
      >
        @{displayName || number}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (parts.length === 0) return content;

  // Aggiungi testo rimanente
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return <>{parts}</>;
}

export const MessageBubble = memo(function MessageBubble({
  message,
  isGroup = false,
  onReply,
  onMediaClick,
  onRefreshMessage,
  onForward,
  onForwardToTeamChat,
  onRetry,
  chatProfilePicUrl,
  chatName,
  resolvedMentionNames,
  isSelectionMode = false,
  isSelected = false,
  onSelectionToggle,
  onStartSelection,
  capabilities = DEFAULT_CAPABILITIES,
}: MessageBubbleProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const smileAnchorRef = useRef<HTMLButtonElement>(null);
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const { confirm } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');

  // P2: Selettori individuali (critico — questo componente renderizza per ogni messaggio)
  const deleteMessageAction = useWhatsAppChatStore(s => s.deleteMessageAction);
  const editMessageAction = useWhatsAppChatStore(s => s.editMessageAction);
  const reactToMessageAction = useWhatsAppChatStore(s => s.reactToMessageAction);
  const storeMessages = useWhatsAppChatStore(s => s.messages);
  const pinMessage = useMessageMarksStore(s => s.pin);
  const unpinMessage = useMessageMarksStore(s => s.unpin);
  const setStar = useMessageMarksStore(s => s.setStar);

  const pending = isPending(message);
  const isOutbound = pending || message.direction === 'outbound';
  const realId = pending ? null : (message as Message).id;
  const isPinned = useMessageMarksStore(s => !!realId && s.marks.pinnedId === realId);
  const isStarred = useMessageMarksStore(s => !!realId && s.marks.starred.includes(realId));
  const caps = capabilities;

  // Mappa JID → nome per risolvere le menzioni (unisce nomi dallo store + nomi risolti via WAHA API)
  const jidNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    // Nomi dai messaggi dello store (senderJid → senderName)
    for (const msg of storeMessages) {
      if (msg.senderJid && msg.senderName) {
        map.set(msg.senderJid, msg.senderName);
      }
    }
    // Nomi risolti da WAHA API (numero → nome) - hanno priorità per i LID
    if (resolvedMentionNames) {
      for (const [num, name] of resolvedMentionNames) {
        // Aggiungi come numero puro per il matching in buildNumberToNameMap
        if (!map.has(num)) {
          map.set(num, name);
        }
      }
    }
    return map;
  }, [storeMessages, resolvedMentionNames]);

  // Sender info per gruppi (solo per messaggi inbound)
  const showSender = isGroup && !isOutbound && !pending && 'senderJid' in message && message.senderJid;
  const senderColor = showSender ? getSenderColor(message.senderJid!) : undefined;

  // Debug temporaneo per capire perché i sender non appaiono (solo gruppi)
  if (import.meta.env.DEV && isGroup && !isOutbound && !pending && message.direction === 'inbound' && !showSender) {
    const msgId = 'id' in message ? message.id : '?';
    debugLog('MessageBubble', `❌ showSender=false in GRUPPO per msg ${msgId?.slice(0,20)}`, {
      hasSenderJidProp: 'senderJid' in message,
      senderJid: (message as any).senderJid,
      senderName: (message as any).senderName,
    }, 'warn');
  }

  // Nome sender: priorità senderName dal messaggio, poi lookup nella cache partecipanti gruppo
  const senderDisplayName = useMemo(() => {
    if (!showSender) return '';
    const msg = message as Message;
    if (msg.senderName) return msg.senderName;
    // Fallback: cerca nella mappa partecipanti gruppo (numero puro senza @suffix)
    if (resolvedMentionNames && msg.senderJid) {
      const number = msg.senderJid.split('@')[0];
      const resolved = resolvedMentionNames.get(number);
      if (resolved) return resolved;
    }
    return undefined; // formatSenderName formatterà il numero
  }, [showSender, message, resolvedMentionNames]);

  // Handle selection click
  const handleSelectionClick = useCallback((e: React.MouseEvent) => {
    if (isSelectionMode && onSelectionToggle) {
      const messageId = 'id' in message ? message.id : (message as PendingMessage).tempId;
      onSelectionToggle(messageId, e.shiftKey);
    }
  }, [isSelectionMode, onSelectionToggle, message]);

  const SelectionCheckbox = () => (
    <div
      className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
        isSelected
          ? 'bg-purple-600 border-purple-600'
          : 'border-[#787774] dark:border-gray-500 hover:border-purple-400'
      }`}
    >
      {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </div>
  );

  const handleReply = useCallback(() => {
    if (!pending && onReply) onReply(message as Message);
  }, [message, pending, onReply]);

  const handleMediaClick = useCallback(() => {
    if (!pending && onMediaClick && message.mediaUrl) onMediaClick(message.mediaUrl);
  }, [message, pending, onMediaClick]);

  const handleEdit = useCallback(() => {
    if (pending) return;
    setEditText((message as Message).content || '');
    setIsEditing(true);
  }, [message, pending]);

  const handleEditConfirm = useCallback(async () => {
    if (!pending && editText.trim()) {
      await editMessageAction((message as Message).id, editText.trim());
    }
    setIsEditing(false);
  }, [message, pending, editText, editMessageAction]);

  const handleDelete = useCallback(async () => {
    if (pending) return;
    // Elimina per tutti e' irreversibile: si chiede conferma (mai window.confirm).
    const ok = await confirm({
      type: 'danger',
      title: 'Eliminare il messaggio per tutti?',
      message: 'Il messaggio sparisce anche dal telefono del contatto. Non si puo\' annullare.',
      confirmText: 'Elimina per tutti',
      cancelText: 'Annulla',
    });
    if (!ok) return;
    await deleteMessageAction((message as Message).id);
  }, [message, pending, deleteMessageAction, confirm]);

  const handleReact = useCallback(async (emoji: string) => {
    if (pending) return;
    // Sul numero Ufficiale l'errore di Meta risale: lo si dice invece di tacere.
    try {
      await reactToMessageAction((message as Message).id, emoji);
    } catch (err) {
      console.error('[MessageBubble] react error:', err);
      toast.error(emoji ? 'Reazione non inviata. Riprova tra poco.' : 'Non sono riuscito a togliere la reazione. Riprova tra poco.');
    }
  }, [message, pending, reactToMessageAction]);

  // Etichetta o selettore: la stessa emoji gia' nostra si toglie.
  const handleToggleReaction = useCallback((emoji: string) => {
    const reactions = (message as Message).reactions?.map((r) => ({ emoji: r.emoji, users: r.senderJids }));
    void handleReact(nextReactionEmoji(reactions, emoji));
  }, [message, handleReact]);

  // Fissa / sblocca / stella: l'azione la fa WAHA sul telefono; se fallisce lo si dice.
  const handlePin = useCallback(async () => {
    if (pending) return;
    try {
      await pinMessage((message as Message).id);
      toast.success('Messaggio fissato in alto');
    } catch (err) {
      console.error('[MessageBubble] pin error:', err);
      toast.error('Non sono riuscito a fissare il messaggio. Riprova tra poco.');
    }
  }, [message, pending, pinMessage]);

  const handleUnpin = useCallback(async () => {
    try {
      await unpinMessage();
    } catch (err) {
      console.error('[MessageBubble] unpin error:', err);
      toast.error('Non sono riuscito a sbloccare il messaggio. Riprova tra poco.');
    }
  }, [unpinMessage]);

  const handleToggleStar = useCallback(async () => {
    if (pending) return;
    try {
      await setStar((message as Message).id, !isStarred);
    } catch (err) {
      console.error('[MessageBubble] star error:', err);
      toast.error('Non sono riuscito a segnare il messaggio come importante. Riprova tra poco.');
    }
  }, [message, pending, setStar, isStarred]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content || '');
      toast.success('Testo copiato');
    } catch {
      toast.error('Copia non riuscita: il browser non permette di usare gli appunti');
    }
  }, [message]);

  const handleForward = useCallback(() => {
    if (!pending && onForward) {
      onForward((message as Message).id);
    }
  }, [message, pending, onForward]);

  const handleForwardToTeamChat = useCallback(() => {
    if (!pending && onForwardToTeamChat) {
      onForwardToTeamChat(message as Message);
    }
  }, [message, pending, onForwardToTeamChat]);

  const mediaTimestamp = message.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const isSticker = !pending && message.type === 'sticker';
  const isImage = !pending && message.type === 'image' && !message.content;
  const isAudio = !pending && (message.type === 'audio' || message.type === 'ptt' || message.type === 'voice');
  const isDeleted = !pending && 'isDeleted' in message && message.isDeleted;
  const isSpecial = !pending && SPECIAL_TYPES.has(message.type);

  const isFailed = pending && message.status === 'failed';
  // Messaggio salvato ma rifiutato da Meta (numero Ufficiale): motivo in italiano da metadata.error.
  const failureReason = !pending && message.status === 'failed' ? (message as Message).failureReason : undefined;
  const failureNote = failureReason ? (
    <p className="flex items-start gap-1 mt-1 text-[11px] leading-snug text-red-600 dark:text-red-300 max-w-[260px]" role="alert">
      <AlertCircle className="w-3 h-3 mt-[1px] flex-shrink-0" />
      <span>{failureReason}</span>
    </p>
  ) : null;
  const isSending = pending && message.status === 'sending';

  const bubbleClass = isOutbound
    ? 'bg-[#D9FDD3] dark:bg-[#005C4B] text-[#111B21] dark:text-[#E9EDEF] ml-auto'
    : 'bg-white dark:bg-gray-800 text-[#37352F] dark:text-white';

  const status = message.status;

  // Sorriso e Rispondi diretti, poi "..." con il resto del menu. Niente reazioni dove WhatsApp non le ammette.
  const isSpecialAction = !pending && (message.type === 'system' || message.type === 'call_log');
  const MenuTrigger = () => (
    <div className="relative flex-shrink-0 flex items-center">
      {!isDeleted && !isSpecialAction && caps.canReact && (
        <button
          ref={smileAnchorRef}
          onClick={(e) => { e.stopPropagation(); setShowReactions((v) => !v); }}
          className={`${showReactions ? 'opacity-100 bg-[rgba(55,53,47,0.08)] dark:bg-white/10' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-opacity text-[#787774]`}
          aria-label="Reagisci"
          aria-haspopup="menu"
          aria-expanded={showReactions}
          {...tip('Reagisci')}
        >
          <Smile className="w-4 h-4" />
        </button>
      )}
      {!isDeleted && !isSpecialAction && (
        <button
          onClick={(e) => { e.stopPropagation(); handleReply(); }}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-opacity text-[#787774]"
          aria-label="Rispondi"
          {...tip('Rispondi')}
        >
          <Reply className="w-4 h-4" />
        </button>
      )}
      {showReactions && !pending && (
        <ReactionPicker
          current={(message as Message).reactions?.find((r) => isMyReaction({ emoji: r.emoji, users: r.senderJids }))?.emoji}
          onPick={handleToggleReaction}
          onClose={() => setShowReactions(false)}
          anchorRef={smileAnchorRef}
          position={isOutbound ? 'right' : 'left'}
        />
      )}
      <button
        ref={menuAnchorRef}
        onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
        className={`${showMenu ? 'opacity-100 bg-[rgba(55,53,47,0.08)] dark:bg-white/10' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-opacity text-[#787774]`}
        aria-label="Azioni sul messaggio"
        aria-haspopup="menu"
        aria-expanded={showMenu}
        {...tip('Rispondi, reagisci, inoltra')}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
          <circle cx="3" cy="8" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>
      {showMenu && !pending && (
        <MessageContextMenu
          messageId={(message as Message).id}
          isOutbound={isOutbound}
          isDeleted={isDeleted}
          onReply={handleReply}
          onEdit={isOutbound && !isDeleted && caps.canEdit && !isSpecial ? handleEdit : undefined}
          onDelete={isOutbound && !isDeleted && caps.canDelete ? handleDelete : undefined}
          onForward={caps.canForward ? handleForward : undefined}
          onForwardToTeamChat={handleForwardToTeamChat}
          onReact={handleReact}
          hideReactions={!caps.canReact}
          onPin={caps.canPin ? handlePin : undefined}
          isPinned={isPinned}
          onUnpin={caps.canPin ? handleUnpin : undefined}
          onCopy={message.content && !SPECIAL_TYPES.has(message.type) ? handleCopy : undefined}
          onToggleStar={caps.canStar ? handleToggleStar : undefined}
          isStarred={isStarred}
          onSelect={onStartSelection && realId ? () => onStartSelection(realId) : undefined}
          onClose={() => setShowMenu(false)}
          anchorRef={menuAnchorRef}
          position={isOutbound ? 'right' : 'left'}
        />
      )}
    </div>
  );

  // ===== CHIAMATA (riga di sistema) =====
  if (!pending && message.type === 'call_log') {
    return <CallLogBubble content={message.content} timestamp={message.timestamp} direction={message.direction} metadata={message.metadata} />;
  }

  // ===== EVENTO DI GRUPPO (riga 'system' scritta dal core: entrate, uscite, nome) =====
  if (!pending && message.type === 'system' && message.content) {
    return (
      <div className="flex justify-center my-1" role="note">
        <div className="px-3 py-1 rounded-lg bg-white/90 dark:bg-gray-800/90 shadow-sm text-xs text-[#787774] dark:text-white/70 text-center max-w-[80%]">
          {message.content}
        </div>
      </div>
    );
  }

  // ===== STICKER =====
  if (isSticker) {
    return (
      <div className={`group flex items-end gap-1 max-w-[80%] min-w-0 ${isOutbound ? 'ml-auto' : 'mr-auto'}`}>
        {isOutbound && !isSelectionMode && <MenuTrigger />}
        <div className="flex flex-col items-end mb-2">
          {showSender && (
            <p className="text-xs font-semibold mb-0.5 truncate self-start" style={{ color: senderColor }}>
              {formatSenderName((message as Message).senderJid!, senderDisplayName)}
            </p>
          )}
          <MessageMedia type={message.type} mediaUrl={message.mediaUrl} mediaMimetype={message.mediaMimetype} onImageClick={handleMediaClick} />
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-sm -mt-1">
            <MessageTime timestamp={message.timestamp} isOutbound={false} />
            {isOutbound && <MessageStatus status={status === 'sending' ? 'pending' : status === 'failed' ? 'failed' : (status as Message['status'])} isOutbound={false} />}
          </div>
        </div>
        {!isOutbound && !isSelectionMode && <MenuTrigger />}
      </div>
    );
  }

  // ===== IMAGE =====
  if (isImage) {
    return (
      <div className={`group flex items-end gap-1 max-w-[80%] min-w-0 ${isOutbound ? 'ml-auto' : 'mr-auto'}`}>
        {isOutbound && !isSelectionMode && <MenuTrigger />}
        <div className="flex flex-col items-end">
          {showSender && (
            <p className="text-xs font-semibold mb-0.5 truncate self-start" style={{ color: senderColor }}>
              {formatSenderName((message as Message).senderJid!, senderDisplayName)}
            </p>
          )}
          <MessageMedia type={message.type} mediaUrl={message.mediaUrl} mediaMimetype={message.mediaMimetype} onImageClick={handleMediaClick} timestamp={mediaTimestamp} />
          {isOutbound && (
            <div className="flex items-center gap-1 mt-0.5">
              <MessageStatus status={status === 'sending' ? 'pending' : status === 'failed' ? 'failed' : (status as Message['status'])} isOutbound={false} />
            </div>
          )}
          {failureNote}
        </div>
        {!isOutbound && !isSelectionMode && <MenuTrigger />}
      </div>
    );
  }

  // ===== AUDIO =====
  if (isAudio) {
    return (
      <div className={`group flex items-end gap-1 max-w-[80%] min-w-0 ${isOutbound ? 'ml-auto' : 'mr-auto'}`}>
        {isOutbound && !isSelectionMode && <MenuTrigger />}
        <div className={`rounded-2xl px-3 py-2 shadow-sm overflow-hidden min-w-0 ${bubbleClass}`}>
          {showSender && (
            <p className="text-xs font-semibold mb-0.5 truncate" style={{ color: senderColor }}>
              {formatSenderName((message as Message).senderJid!, senderDisplayName)}
            </p>
          )}
          <MessageMedia type={message.type} mediaUrl={message.mediaUrl} mediaMimetype={message.mediaMimetype} onImageClick={handleMediaClick} timestamp={mediaTimestamp} avatarUrl={chatProfilePicUrl} avatarName={chatName} />
          {!pending && WHATSAPP_AUDIO_TRANSCRIPTION_ENABLED && (message as Message).transcription && (
            <AudioTranscription text={(message as Message).transcription!} isOutbound={isOutbound} />
          )}
          {isOutbound && (
            <div className="flex items-center justify-end mt-1">
              <MessageStatus status={status === 'sending' ? 'pending' : status === 'failed' ? 'failed' : (status as Message['status'])} isOutbound={isOutbound} />
            </div>
          )}
          {failureNote}
        </div>
        {!isOutbound && !isSelectionMode && <MenuTrigger />}
      </div>
    );
  }

  // ===== DEFAULT (testo, documenti, ecc.) =====
  const hasReactions = !pending && 'reactions' in message && !!message.reactions?.length;
  return (
    <div
      className={`group flex items-center gap-2 max-w-[85%] min-w-0 ${isOutbound ? 'ml-auto' : 'mr-auto'} ${hasReactions ? 'mb-4' : ''} ${
        isSelectionMode ? 'cursor-pointer' : ''
      } ${isSelected ? 'bg-purple-100/50 dark:bg-purple-900/20 -mx-2 px-2 py-1 rounded-lg' : ''}`}
      onClick={isSelectionMode ? handleSelectionClick : undefined}
    >
      {isSelectionMode && !isOutbound && <SelectionCheckbox />}

      <div className={`flex items-end gap-1 flex-1 min-w-0 ${isOutbound ? 'justify-end' : ''}`}>
        {/* Context menu trigger (left for outbound) */}
        {!isSelectionMode && isOutbound && !pending && <MenuTrigger />}

        <div className="relative min-w-0">
        <div className={`rounded-2xl px-2.5 py-1.5 shadow-sm overflow-hidden min-w-[140px] ${bubbleClass} ${isSending ? 'opacity-70' : ''} ${isFailed ? 'ring-1 ring-red-400/60' : ''}`}>
          {showSender && (
            <p className="text-xs font-semibold mb-0.5 truncate" style={{ color: senderColor }}>
              {formatSenderName((message as Message).senderJid!, senderDisplayName)}
            </p>
          )}

          {'replyToBody' in message && message.replyToBody && (
            <MessageReply replyToBody={message.replyToBody} isOutbound={isOutbound} />
          )}

          {!pending && message.type === 'ciphertext' ? (
            <div className="flex items-center gap-2 py-1">
              <Lock className={`w-4 h-4 ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`} />
              <p className={`text-sm italic ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`}>In attesa di decrittazione...</p>
              {onRefreshMessage && (
                <button onClick={() => onRefreshMessage((message as Message).id)} className={`p-1 rounded hover:bg-black/10 ${isOutbound ? 'text-[#027EB5] dark:text-[#53BDEB]' : 'text-[#2383E2]'}`}>
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : isDeleted ? (
            <div className="flex items-center gap-2 py-1">
              <Ban className={`w-4 h-4 ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`} />
              <p className={`text-sm italic ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`}>Questo messaggio è stato eliminato</p>
            </div>
          ) : isEditing ? (
            <div className="flex flex-col gap-1 py-1">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="text-sm bg-white/60 dark:bg-black/20 border border-[rgba(55,53,47,0.2)] dark:border-white/30 rounded px-2 py-1 resize-none outline-none text-inherit w-full"
                rows={2}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditConfirm(); }
                  // preventDefault: Esc annulla solo la modifica, non chiude il pannello.
                  if (e.key === 'Escape') { e.preventDefault(); setIsEditing(false); }
                }}
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setIsEditing(false)} className="text-[11px] opacity-60 hover:opacity-90">Annulla</button>
                <button onClick={handleEditConfirm} className="text-[11px] font-medium text-[#2383E2] dark:text-[#53BDEB] hover:opacity-80">Salva</button>
              </div>
            </div>
          ) : (
            <>
              {isSpecial && message.type === 'location' && (
                <LocationContent messageId={(message as Message).id} content={message.content} metadata={(message as Message).metadata} isOutbound={isOutbound} />
              )}
              {isSpecial && message.type === 'contact' && (
                <ContactContent messageId={(message as Message).id} content={message.content} metadata={(message as Message).metadata} isOutbound={isOutbound} />
              )}
              {isSpecial && message.type === 'poll' && (
                <PollContent messageId={(message as Message).id} content={message.content} isOutbound={isOutbound} />
              )}
              {!pending && !isSpecial && (message.type !== 'text' || message.mediaUrl) && (
                <MessageMedia type={message.type} mediaUrl={message.mediaUrl} mediaMimetype={message.mediaMimetype} mediaFilename={message.mediaFilename} onImageClick={handleMediaClick} timestamp={mediaTimestamp} avatarUrl={chatProfilePicUrl} avatarName={chatName} />
              )}
              {message.content && !isSpecial && (
                <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere" style={{ wordBreak: 'break-word' }}>
                  {renderContentWithLinks(
                    message.content,
                    isOutbound,
                    isGroup && !pending ? jidNamesMap : undefined,
                    isGroup
                  )}
                  {/* Inline timestamp — WhatsApp style: floats to the right of last text line */}
                  {!isSending && !isFailed && (
                    <span className="inline-flex float-right items-center gap-1 ml-2 relative" style={{ top: '4px' }}>
                      {isStarred && <Star className="w-3 h-3 fill-current text-amber-500" aria-label="Importante" />}
                      {!pending && 'isEdited' in message && message.isEdited && (
                        <span className={`text-[10px] ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`}>modificato</span>
                      )}
                      <MessageTime timestamp={message.timestamp} isOutbound={isOutbound} />
                      {isOutbound && (
                        <MessageStatus status={status === 'sending' ? 'pending' : status === 'failed' ? 'failed' : (status as Message['status'])} isOutbound={isOutbound} />
                      )}
                    </span>
                  )}
                </p>
              )}
            </>
          )}

          {/* Footer — solo per messaggi senza testo, o stati sending/failed */}
          {(!message.content || isSpecial || isSending || isFailed) && (
            <div className="flex items-center justify-end gap-1 mt-0.5">
              {isStarred && <Star className="w-3 h-3 fill-current text-amber-500" aria-label="Importante" />}
              {!pending && 'isEdited' in message && message.isEdited && (
                <span className={`text-[10px] ${isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]'}`}>modificato</span>
              )}
              {isSending ? (
                <>
                  <Clock className="w-3 h-3 text-[#667781]/60 dark:text-[#8696A0]/60 animate-pulse" />
                  <span className="text-[10px] text-[#667781]/60 dark:text-[#8696A0]/60">Invio...</span>
                </>
              ) : isFailed ? (
                <>
                  <AlertCircle className="w-3 h-3 text-red-300" />
                  <span className="text-[10px] text-red-300">Invio fallito</span>
                  {onRetry && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onRetry((message as PendingMessage).tempId); }}
                      className="text-[10px] text-red-200 hover:text-white underline flex items-center gap-0.5 ml-1"
                    >
                      <RotateCcw className="w-2.5 h-2.5" />
                      Riprova
                    </button>
                  )}
                </>
              ) : (
                <>
                  <MessageTime timestamp={message.timestamp} isOutbound={isOutbound} />
                  {isOutbound && (
                    <MessageStatus status={status === 'sending' ? 'pending' : status === 'failed' ? 'failed' : (status as Message['status'])} isOutbound={isOutbound} />
                  )}
                </>
              )}
            </div>
          )}

          {/* Motivo dell'invio fallito (numero Ufficiale) */}
          {failureNote}
        </div>

        {hasReactions && (
          <MessageReactions
            reactions={(message as Message).reactions!.map((r) => ({ emoji: r.emoji, count: r.count, users: r.senderJids }))}
            isOutbound={isOutbound}
            resolveName={(jid) => reactionSenderName(jid, { isGroup, chatName, names: jidNamesMap, formatNumber: (j) => formatSenderName(j) })}
            onToggle={caps.canReact ? handleToggleReaction : undefined}
          />
        )}
        </div>

        {/* Context menu trigger (right for inbound) */}
        {!isSelectionMode && !isOutbound && !pending && <MenuTrigger />}
      </div>

      {isSelectionMode && isOutbound && <SelectionCheckbox />}
    </div>
  );
});

function AudioTranscription({ text, isOutbound }: { text: string; isOutbound: boolean }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-1.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-1 text-[11px] font-medium transition-colors ${
          isOutbound ? 'text-[#667781] dark:text-[#8696A0] hover:text-[#111B21] dark:hover:text-white' : 'text-[#787774] hover:text-[#37352F] dark:hover:text-white'
        }`}
      >
        <FileText className="w-3 h-3" />
        Trascrizione
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <p className={`mt-1 text-xs leading-relaxed ${isOutbound ? 'text-[#111B21] dark:text-[#E9EDEF]' : 'text-[#37352F] dark:text-gray-300'}`}>
          {text}
        </p>
      )}
    </div>
  );
}
